import React from 'react';
import { Button, Grid } from '@mui/material';
import { Timeline } from '@mui/lab';

import { AuthorizerContext } from 'app/providers/acl/AclProvider';
import { type LogsType } from 'app/main/components/hooks/useLogs';
import useSettings from 'app/main/components/hooks/useSettings';
import useIsModuleDisabled from 'app/main/components/hooks/useIsModuleDisabled';
import Icon from 'app/main/components/Icon';
import ModuleSearchField from 'app/main/components/fields/ModuleSearchField';
import { getRelationsTypes } from 'app/main/modules/components/FlowRelationsTypes';

import DetailsSectionInfoWrapper from './DetailsSectionInfoWrapper';
import FlowItem from '../components/FlowItem';
import FlowAddParentDialog from '../components/FlowAddParentDialog';

export interface RelationType {
	module: string;
	name: string;
	label: string;
	uid: string;
	title: string;
	internalNumber?: string;
	date?: string;
	deleted?: boolean;
	current?: boolean;
}

export interface DetailsSectionFlowProps {
	module: string;
	model: any;
	isEdit: boolean;
	sendChange: (name: string, newValue: string | null) => void;
	logProps?: LogsType;
}

interface DetailsSectionFlowEditProps {
	module: string;
	itemUid: string;
	currentRelations: RelationType[];
	newRelations: RelationType[];
	sendChange: (name: string, newValue: string | null) => void;
}

function DetailsSectionFlowEdit(props: DetailsSectionFlowEditProps) {
	const { t } = useSettings();

	const [showAddParentDialog, setShowAddParentDialog] = React.useState(false);

	return (
		<>
			{props.currentRelations.map(
				(relation, i) =>
					!relation.current && (
						<Grid key={i} size={12}>
							<ModuleSearchField
								name={relation.name}
								label={t(relation.label)}
								module={relation.module}
								uid={relation.uid}
								isEdit
								sendChange={props.sendChange}
							/>
						</Grid>
					)
			)}
			{props.newRelations.length > 0 && (
				<Grid size={12}>
					<Button startIcon={<Icon icon="add-circle" />} onClick={() => setShowAddParentDialog(true)}>
						{t('DETAILS_FLOW_SECTION_BUTTON_ADD')}
					</Button>
				</Grid>
			)}
			{showAddParentDialog && (
				<FlowAddParentDialog
					open={showAddParentDialog}
					closeDialog={() => setShowAddParentDialog(false)}
					module={props.module}
					itemUid={props.itemUid}
					newRelations={props.newRelations}
					sendChange={props.sendChange}
				/>
			)}
		</>
	);
}

function DetailsSectionFlowView({ currentRelations, logProps }) {
	// Sorts relations by date
	currentRelations = currentRelations.sort((a, b) => {
		if (a.current) return 1;
		if (a.date < b.date) return -1;
		if (a.date > b.date) return 1;
		return 0;
	});

	return (
		<Grid size={12}>
			<Timeline className="w-full p-0">
				{currentRelations.map((relation, i) => (
					<FlowItem key={i} {...relation} isLast={i === currentRelations.length - 1} logProps={logProps} />
				))}
			</Timeline>
		</Grid>
	);
}

export function DetailsSectionFlowContent(props: DetailsSectionFlowProps) {
	const { settings } = useSettings(props.module);
	const isModuleDisabled = useIsModuleDisabled();
	const authorizer = React.useContext(AuthorizerContext);

	// Get relation types
	let relationsTypes = getRelationsTypes(settings.modu_short);

	// Get current element
	const currentElement = relationsTypes.find(relationType => relationType.module === props.module);

	// Add parent elements to relation types
	relationsTypes = relationsTypes.map(relationType => {
		if (relationType.module === props.module) {
			return {
				module: props.module,
				label: relationType.label,
				uidField: `${settings.modu_short}_parent_${settings.modu_short}_uid`,
				titleField: `parent_${relationType.titleField}`,
				dateField: `parent_${relationType.dateField}`,
				deletedField: `parent_${relationType.deletedField}`
			};
		}
		return relationType;
	});

	// Add current element to relation types
	if (currentElement) {
		relationsTypes.push({
			...currentElement,
			uidField: `${settings.modu_short}_uid`,
			current: true
		});
	}

	// Create relations from relation types
	const relations = relationsTypes.map(relationType => ({
		module: relationType.module,
		name: relationType.uidField,
		label: relationType.label,
		uid: props.model[relationType.uidField],
		title: props.model[relationType.titleField],
		date: relationType.dateField ? props.model[relationType.dateField] : undefined,
		deleted: props.model[relationType.deletedField],
		current: relationType.current
	}));

	// Get current relations and new relations
	const currentRelations: RelationType[] = [];
	const newRelations: RelationType[] = [];

	relations.forEach(relation => {
		if (relation.uid) currentRelations.push(relation);
		else if (!isModuleDisabled(relation.module) && authorizer.can('update', relation.module))
			newRelations.push(relation);
	});

	return props.isEdit ? (
		<DetailsSectionFlowEdit
			module={props.module}
			itemUid={props.model[`${settings.modu_short}_uid`]}
			currentRelations={currentRelations}
			newRelations={newRelations}
			sendChange={props.sendChange}
		/>
	) : (
		<DetailsSectionFlowView currentRelations={currentRelations} logProps={props.logProps} />
	);
}

export default function DetailsSectionFlow(props: DetailsSectionFlowProps) {
	const { t } = useSettings();

	return (
		<DetailsSectionInfoWrapper
			avatar={<Icon icon="timeline-text-outline" />}
			title={t('DETAILS_FLOW_SECTION')}
			subheader={t('DETAILS_FLOW_SUBHEADER')}
		>
			<DetailsSectionFlowContent
				module={props.module}
				model={props.model}
				isEdit={props.isEdit}
				sendChange={props.sendChange}
				logProps={props.logProps}
			/>
		</DetailsSectionInfoWrapper>
	);
}
